import { LookupDto } from 'api/openapi';
import { CloseIcon } from 'app/assets/icons';
import React, { useState, ReactNode, useRef, KeyboardEvent } from 'react';
import SimpleReactValidator from 'simple-react-validator';
import styled from 'styled-components';
import { RectangleSkeletonLoader } from '../SkeletonLoader/RectangleSkeletonLoader';
import { CommonMessages } from 'locales/CommonMessage/messages';
import { useTranslation } from 'react-i18next';

export type DropdownOption = {
  id: string | number;
  label: string;
  value: string | number;
};

type DropdownProps = {
  options: LookupDto[];
  value?: LookupDto;
  onChange?: (option: LookupDto) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  hideStar?: boolean;
  error?: string;
  className?: string;
  dropDownChildren?: ReactNode;
  isSearchNotRequired?: boolean;
  clearable?: boolean;
  onClearClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  validator?: SimpleReactValidator;
  validations?: string;
  name?: string;
  required?: boolean;
  customValidation?: (value: any) => boolean;
  customErrorMessage?: string;
  validationLabel?: string;
  isLoader?: boolean;
  hideLabel?: boolean;
  readOnly?: boolean;
  loaderHeight?: number;
  loaderClassName?: string;
  // New props for add button functionality
  onAddClick?: (searchTerm: string) => void;
  showAddButton?: boolean;
  addButtonTooltip?: string;
};

const Dropdown = ({
  options,
  value,
  onChange,
  label,
  placeholder = 'Select an option',
  disabled = false,
  hideStar,
  className = '',
  dropDownChildren,
  isSearchNotRequired,
  clearable,
  onClearClick,
  validator,
  validations,
  required = false,
  validationLabel,
  isLoader,
  hideLabel,
  readOnly,
  loaderHeight,
  loaderClassName,
  onAddClick,
  showAddButton = true,
  addButtonTooltip = 'Add as new item',
}: DropdownProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const optionsRef = useRef<(HTMLLIElement | null)[]>([]);

  // Initialize validator if not provided
  const simpleValidator = React.useRef(validator);
  const [validateField, setValidateField] = useState(false);

  const handleSelect = (option: LookupDto) => {
    onChange?.(option);
    setIsOpen(false);
    setSearchTerm('');
    setFocusedIndex(-1);
  };

  const handleClearClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    onClearClick?.(e);
  };

  const handleAddClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    if (onAddClick && searchTerm.trim()) {
      onAddClick(searchTerm.trim());
    }
  };

  const handleBlur = e => {
    e.preventDefault();
    if (!e?.target?.value && validations) {
      simpleValidator?.current?.showMessageFor(
        validationLabel?.toString()!
          ? validationLabel?.toString()!
          : label?.toString()!
          ? label?.toString()!
          : 'field',
      );
      setValidateField(!validateField);
    }
  };

  const handleKeyDown = (
    e: KeyboardEvent<HTMLButtonElement | HTMLDivElement>,
  ) => {
    if (disabled || readOnly) return;

    switch (e.key) {
      case 'Enter':
        e.preventDefault();
        if (isOpen && focusedIndex >= 0) {
          handleSelect(filteredOptions[focusedIndex]);
        } else {
          setIsOpen(!isOpen);
        }
        break;
      case ' ':
        if (!isOpen) {
          e.preventDefault();
          setIsOpen(true);
        }
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          setFocusedIndex(prev =>
            prev < filteredOptions.length - 1 ? prev + 1 : prev,
          );
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (isOpen) {
          setFocusedIndex(prev => (prev > 0 ? prev - 1 : prev));
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setFocusedIndex(-1);
        break;
      case 'Tab':
        if (isOpen) {
          setIsOpen(false);
          setFocusedIndex(-1);
        }
        break;
    }
  };

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setFocusedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [value]);

  React.useEffect(() => {
    const filtered = options?.filter(option =>
      option?.name?.toLowerCase().includes(searchTerm.toLowerCase()),
    );
    setFilteredOptions(filtered);
    setFocusedIndex(-1);
  }, [searchTerm, options]);

  React.useEffect(() => {
    if (isOpen && focusedIndex >= 0 && optionsRef.current[focusedIndex]) {
      optionsRef.current[focusedIndex]?.scrollIntoView({
        block: 'nearest',
      });
    }
  }, [focusedIndex, isOpen]);

  const isInvalidValue =
    value && options ? options?.some(option => option.id === value?.id) : true;

  // Check if we should show the add button
  const shouldShowAddButton =
    showAddButton &&
    onAddClick &&
    searchTerm !== '' &&
    filteredOptions.length === 0 &&
    !isSearchNotRequired;

  return (
    <DropdownWrapper
      className={`${className} ${clearable ? 'clearable' : ''}`}
      ref={dropdownRef}
      onKeyDown={handleKeyDown}
    >
      {label && !hideLabel && (
        <Label>
          {label}
          {(required || validations) && !hideStar && !disabled && !readOnly && (
            <RequiredStar>*</RequiredStar>
          )}
        </Label>
      )}

      {isLoader ? (
        <RectangleSkeletonLoader
          className={`field-skeleton ${loaderClassName ? loaderClassName : ''}`}
          count={1}
          height={loaderHeight ? loaderHeight : 40}
        />
      ) : (
        <DropdownButton
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          onBlur={handleBlur}
          disabled={disabled || readOnly}
          aria-invalid={true}
          aria-required={required}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          className="dropdown-button"
        >
          <span
            className={`${
              readOnly ? 'read-only' : value ? 'value' : 'placeholder'
            }`}
          >
            {value ? value?.name : disabled ? '' : placeholder}
          </span>

          {!disabled &&
            !readOnly &&
            (value && clearable ? (
              <ClearButton onClick={handleClearClick} className="clear-button">
                {isInvalidValue && <CloseIcon />}
              </ClearButton>
            ) : (
              <ArrowIcon isOpen={isOpen}>
                <svg
                  width="12"
                  height="6"
                  viewBox="0 0 12 6"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M1 0.674316L6 5.32548L11 0.674316"
                    stroke="#A0AEC0"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </ArrowIcon>
            ))}
        </DropdownButton>
      )}
      {!disabled && !readOnly && (
        <ErrorText className="error-text">
          {!isInvalidValue &&
            !isLoader &&
            `The specified ${label?.toLowerCase()} is no longer available.`}
          {simpleValidator?.current?.message(
            validationLabel?.toString()
              ? validationLabel?.toString()
              : label?.toString()
              ? label?.toString()
              : 'field',
            value?.toString() && value?.toString(),
            validations ?? '',
          )}
        </ErrorText>
      )}

      {isOpen && (
        <DropdownMenu
          className="dropdown-menu"
          role="listbox"
          aria-label={label || 'dropdown menu'}
        >
          {!isSearchNotRequired && (
            <SearchWrapper>
              <SearchInput
                type="text"
                placeholder={t(CommonMessages.search())}
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                ref={searchInputRef}
              />
              {shouldShowAddButton && (
                <AddButton
                  type="button"
                  onClick={handleAddClick}
                  title={addButtonTooltip}
                  className="add-button"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8 3.5V12.5M3.5 8H12.5"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </AddButton>
              )}
            </SearchWrapper>
          )}

          <OptionsList>
            {dropDownChildren ? (
              filteredOptions.map(option => (
                <React.Fragment key={option.id}>
                  {dropDownChildren}
                </React.Fragment>
              ))
            ) : (
              <>
                {filteredOptions.map((option, index) => (
                  <OptionItem
                    key={option.id}
                    onClick={() => handleSelect(option)}
                    isSelected={value?.id === option.id}
                    isFocused={index === focusedIndex}
                    ref={el => (optionsRef.current[index] = el)}
                    role="option"
                    aria-selected={value?.id === option.id}
                  >
                    {option?.name}
                  </OptionItem>
                ))}
                {filteredOptions.length === 0 && (
                  <EmptyMessage>
                    {t(CommonMessages.noOptionsFound())}
                  </EmptyMessage>
                )}
              </>
            )}
          </OptionsList>
        </DropdownMenu>
      )}
    </DropdownWrapper>
  );
};

const AddButton = styled.button`
  position: absolute;
  right: 13px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #b8c3d0;
  border-radius: 6px;

  &:hover {
    background: #bcc3ca;
    color: #333342;
  }
`;

const DropdownWrapper = styled.div`
  position: relative;
  width: 100%;
  font-family: 'Open Sans';
  &.edit-loan-component {
    min-height: 90px;
    @media (max-width: 768px) {
      min-height: 120px;
    }
  }
  &.add-user-component {
    margin-top: 30px;
    min-height: 65px;
    .dropdown-menu {
      top: 40px;
    }
  }
  &.add-facility-component {
    min-height: 90px;
  }
  &.add-lc-component {
    min-height: 90px;
  }
  &.add-sub-limit-component {
    min-height: 90px;
  }
  .value {
    @media (min-width: 768px) {
      width: 190px;
      overflow-wrap: break-word;
      word-break: break-all;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
`;

const Label = styled.label`
  display: block;
  margin-bottom: 5px;
  font-size: 14px;
  font-weight: 400;
  margin-left: 4px;

  font-family: 'Open Sans';
  color: ${p => p.theme.datePickerTitle};
  line-height: 19.6px;
`;

const RequiredStar = styled.span`
  color: ${p => p.theme.dropDownRequiredColor};
  margin-left: 4px;
`;

const DropdownButton = styled.button`
  min-height: 40px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0px 18px 0px 20px;
  background: ${props =>
    props.disabled ? props.theme.disabledBgClr : props.theme.white};
  border: 1px solid
    ${props => (props.disabled ? props.theme.disabledBrdrClr : '#E2E8F0')};
  border-radius: 5px;
  color: ${props =>
    props.disabled
      ? props.theme.textFieldDisableTextColor
      : props.theme.cardValueClr};
  font-size: 12px;
  line-height: 24px;
  font-weight: 600;
  text-align: left;
  font-family: 'Open Sans';
  .placeholder {
    max-width: 320px;
    overflow-wrap: break-word;
    word-break: break-all;
    color: ${p => p.theme.dropDownPlaceholderColor};
    font-weight: 400 !important;
    font-family: 'Open Sans';
  }
  .read-only {
    color: ${p => p.theme.cardValueClr};
    cursor: text;
  }
  cursor: default;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
  }
`;

const ArrowIcon = styled.span<{ isOpen: boolean }>`
  display: flex;
  align-items: center;
  svg {
    width: 12px;
    height: 6px;
    color: #9ca3af;
    transform: ${props => (props.isOpen ? 'rotate(180deg)' : 'rotate(0)')};
    transition: transform 0.2s ease;
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 67px;
  left: 0;
  right: 0;
  margin-top: 2px;
  background: ${p => p.theme.white};
  border: 1px solid ${p => p.theme.dropDownMenuBorderColor};
  border-radius: 5px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
  z-index: 50;
`;

const SearchWrapper = styled.div`
  padding: 8px;
  border-bottom: 1px solid ${p => p.theme.dropDownMenuBorderColor};
  position: relative;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid ${p => p.theme.dropDownMenuBorderColor};
  border-radius: 6px;
  font-size: 14px;
  line-height: 20px;
  color: ${p => p.theme.dropDownLabelColor};

  &:focus {
    outline: none;
    border-color: ${p => p.theme.dropDownFocusBorderColor};
  }

  &::placeholder {
    color: ${p => p.theme.dropDownPlaceholderColor};
  }
`;

const OptionsList = styled.ul`
  max-height: 240px;
  overflow-y: auto;
  padding: 4px 0;
  margin: 0;
  list-style: none;
  ::-webkit-scrollbar {
    width: 5px;
    margin-left: 3px;
  }
  ::-webkit-scrollbar-track {
    background: transparent !important;
    border-radius: 10px;
  }
  ::-webkit-scrollbar-thumb {
    width: 9px;
    background: ${p => p.theme.dropDownMenuScrollThumbColor} !important;
    border-radius: 10px;
  }
`;

const OptionItem = styled.li<{ isSelected: boolean; isFocused: boolean }>`
  padding: 8px 12px;
  font-size: 14px;
  line-height: 20px;
  color: ${p => p.theme.black};
  cursor: pointer;
  background: ${props => {
    if (props.isSelected) return '#f3f4f6';
    if (props.isFocused) return p => p.theme.dropDownBackGroundColor;
    return 'transparent';
  }};

  &:hover {
    background: ${p => p.theme.dropDownBackGroundColor};
  }
`;

const EmptyMessage = styled.li`
  padding: 8px 12px;
  font-size: 14px;
  color: ${p => p.theme.black};
  text-align: center;
`;

const ClearButton = styled.button`
  z-index: 10;
  cursor: pointer;
  display: flex;
  align-items: center;
  border: none;
  background: transparent;
  &:focus {
    box-shadow: none;
    outline: 0;
  }
`;

const ErrorText = styled.span`
  display: block;
  font-size: 13px !important;
  color: ${p => p.theme.errorStarRed};
  padding-top: 4px !important;
  text-align: left;
  font-family: 'Open Sans';
`;

export default Dropdown;
